import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

type FeatureBoardStatus =
  | 'under_consideration'
  | 'planned'
  | 'in_progress'
  | 'shipped'
  | 'declined';

interface FeatureBoardItem {
  id: string;
  title: string;
  description: string;
  status: FeatureBoardStatus;
  visible: boolean;
  sortOrder: number;
  voteCount: number;
  createdAt: string;
  updatedAt: string;
}

interface FeatureBoardWriteRequest {
  title?: string;
  description?: string;
  status?: FeatureBoardStatus;
  visible?: boolean;
  sortOrder?: number;
}

const STATUS_OPTIONS: FeatureBoardStatus[] = [
  'under_consideration',
  'planned',
  'in_progress',
  'shipped',
  'declined',
];

const STATUS_STYLES: Record<FeatureBoardStatus, string> = {
  under_consideration: 'bg-gray-50 text-gray-700 ring-gray-500/20',
  planned: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  in_progress: 'bg-yellow-50 text-yellow-800 ring-yellow-600/20',
  shipped: 'bg-green-50 text-green-700 ring-green-600/20',
  declined: 'bg-red-50 text-red-700 ring-red-600/20',
};

function statusLabel(status: FeatureBoardStatus): string {
  return status.replace(/_/g, ' ');
}

async function fetchFeatureBoard() {
  const response = await fetch('/api/v1/admin/feature-board');
  if (!response.ok) {
    throw new Error('Failed to fetch feature board');
  }
  return response.json() as Promise<{ items: FeatureBoardItem[] }>;
}

async function createFeatureBoardItem(request: FeatureBoardWriteRequest) {
  const response = await fetch('/api/v1/admin/feature-board', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    throw new Error('Failed to create feature board item');
  }
  return response.json();
}

async function updateFeatureBoardItem(id: string, request: FeatureBoardWriteRequest) {
  const response = await fetch(`/api/v1/admin/feature-board/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    throw new Error('Failed to update feature board item');
  }
  return response.json();
}

async function deleteFeatureBoardItem(id: string) {
  const response = await fetch(`/api/v1/admin/feature-board/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Failed to delete feature board item');
  }
  return response.json();
}

interface EditorState {
  title: string;
  description: string;
  status: FeatureBoardStatus;
  visible: boolean;
  sortOrder: string;
}

const EMPTY_EDITOR: EditorState = {
  title: '',
  description: '',
  status: 'under_consideration',
  visible: true,
  sortOrder: '0',
};

export default function FeatureBoardPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-feature-board'],
    queryFn: fetchFeatureBoard,
  });

  const [showEditor, setShowEditor] = useState(false);
  const [editorFor, setEditorFor] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-feature-board'] });
  };

  const createMutation = useMutation({
    mutationFn: createFeatureBoardItem,
    onSuccess: () => {
      invalidate();
      closeEditor();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; request: FeatureBoardWriteRequest }) =>
      updateFeatureBoardItem(input.id, input.request),
    onSuccess: () => {
      invalidate();
      closeEditor();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFeatureBoardItem,
    onSuccess: invalidate,
  });

  const toggleVisibleMutation = useMutation({
    mutationFn: (input: { id: string; visible: boolean }) =>
      updateFeatureBoardItem(input.id, { visible: input.visible }),
    onSuccess: invalidate,
  });

  const openCreate = () => {
    setEditorFor(null);
    setEditor(EMPTY_EDITOR);
    setShowEditor(true);
  };

  const openEdit = (item: FeatureBoardItem) => {
    setEditorFor(item.id);
    setEditor({
      title: item.title,
      description: item.description,
      status: item.status,
      visible: item.visible,
      sortOrder: String(item.sortOrder),
    });
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditorFor(null);
    setEditor(EMPTY_EDITOR);
  };

  const submitEditor = () => {
    const title = editor.title.trim();
    if (title.length === 0 || title.length > 200) {
      alert('Title must be between 1 and 200 characters');
      return;
    }
    const sortOrder = Number.parseInt(editor.sortOrder || '0', 10);
    if (!Number.isInteger(sortOrder) || sortOrder < 0) {
      alert('Sort order must be a non-negative integer');
      return;
    }

    const request: FeatureBoardWriteRequest = {
      title,
      description: editor.description.trim(),
      status: editor.status,
      visible: editor.visible,
      sortOrder,
    };

    if (editorFor) {
      updateMutation.mutate({ id: editorFor, request });
    } else {
      createMutation.mutate(request);
    }
  };

  const handleDelete = (item: FeatureBoardItem) => {
    if (confirm(`Delete feature board item "${item.title}"? This cannot be undone.`)) {
      deleteMutation.mutate(item.id);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Feature Board
          </h2>
          <p className="mt-2 text-sm text-gray-500">Curate the in-app feature voting board</p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          New Board Item
        </button>
      </div>

      <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-2 text-sm text-gray-500">Loading feature board...</p>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">
                Error loading feature board: {error.message}
              </p>
            </div>
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p className="mt-2 text-sm">No feature board items yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                    Title
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Votes
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Visible
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Sort
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Updated
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.items.map((item) => (
                  <tr key={item.id}>
                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {item.title}
                      {item.description && (
                        <p className="mt-0.5 max-w-md truncate text-xs font-normal text-gray-500">
                          {item.description}
                        </p>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[item.status]}`}>
                        {statusLabel(item.status)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {item.voteCount}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={item.visible}
                          onChange={(event) =>
                            toggleVisibleMutation.mutate({
                              id: item.id,
                              visible: event.target.checked,
                            })
                          }
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </label>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {item.sortOrder}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(item.updatedAt).toLocaleDateString()}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <button
                        onClick={() => openEdit(item)}
                        className="text-indigo-600 hover:text-indigo-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      {showEditor ? (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editorFor ? 'Edit Board Item' : 'New Board Item'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  value={editor.title}
                  onChange={(event) => setEditor({ ...editor, title: event.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                  placeholder="Sleep trend charts"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={editor.description}
                  onChange={(event) => setEditor({ ...editor, description: event.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                  rows={3}
                  maxLength={2000}
                  placeholder="Shown when a user taps the tile"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={editor.status}
                  onChange={(event) =>
                    setEditor({ ...editor, status: event.target.value as FeatureBoardStatus })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {statusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Sort Order</label>
                <input
                  type="number"
                  min="0"
                  value={editor.sortOrder}
                  onChange={(event) => setEditor({ ...editor, sortOrder: event.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                />
              </div>
              <div className="flex items-center">
                <input
                  id="board-item-visible"
                  type="checkbox"
                  checked={editor.visible}
                  onChange={(event) => setEditor({ ...editor, visible: event.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <label htmlFor="board-item-visible" className="ml-2 block text-sm text-gray-700">
                  Visible on the public board
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeEditor}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={submitEditor}
                disabled={createMutation.isPending || updateMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? 'Saving...'
                  : editorFor
                    ? 'Save'
                    : 'Create'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
